import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import { BrandSchema, BrandOAuthProviderSchema } from "./types"
import { Response } from "@/types"

// Brand API Response Types
export interface BrandResponse extends BrandSchema {}
export interface BrandOAuthProviderResponse extends BrandOAuthProviderSchema {}

// Brand Request Types
export interface CreateBrandRequest {
  name: string
  description: string
}

export interface UpdateBrandRequest {
  name?: string
  description?: string
}

// OAuth Provider Request Types
export interface CreateBrandOAuthProviderRequest {
  name: string
  provider: string
  client_id: string
  client_secret: string
  scopes: string[]
}

export interface UpdateBrandOAuthProviderRequest {
  name?: string
  client_id?: string
  client_secret?: string
  scopes?: string[]
}

// Response Transformers
export {
  transToBrand as brandResponseToBrand,
  transToBrands as brandsResponseToBrands,
  transToBrandOAuthProvider as oauthProviderResponseToOAuthProvider,
  transToBrandOAuthProviders as oauthProvidersResponseToOAuthProviders,
} from "./types"

// ===== Brand API Hooks =====

export const useGetBrandsApi = (organizationId: string) => {
  return useCodeerApi<Response<BrandResponse[]>>(
    `/organizations/${organizationId}/brands`,
    FetchMethod.Get
  )
}

export const useGetBrandApi = (organizationId: string, brandId: string) => {
  return useCodeerApi<Response<BrandResponse>>(
    `/organizations/${organizationId}/brands/${brandId}`,
    FetchMethod.Get
  )
}

export const useCreateBrandApi = (organizationId: string) => {
  return useCodeerApi<Response<BrandResponse>, CreateBrandRequest>(
    `/organizations/${organizationId}/brands`,
    FetchMethod.Post
  )
}

export const useUpdateBrandApi = (organizationId: string, brandId: string) => {
  return useCodeerApi<Response<BrandResponse>, UpdateBrandRequest>(
    `/organizations/${organizationId}/brands/${brandId}`,
    FetchMethod.Put
  )
}

export const useDeleteBrandApi = (organizationId: string, brandId: string) => {
  return useCodeerApi<Response<unknown>>(
    `/organizations/${organizationId}/brands/${brandId}`,
    FetchMethod.Delete
  )
}

export const useUploadBrandLogoApi = (
  organizationId: string,
  brandId: string
) => {
  return useCodeerApi<Response<BrandResponse>, FormData>(
    `/organizations/${organizationId}/brands/${brandId}/logo`,
    FetchMethod.Post
  )
}

// ===== Brand OAuth Provider API Hooks =====

export const useGetBrandOAuthProvidersApi = (
  organizationId: string,
  brandId: string
) => {
  return useCodeerApi<Response<BrandOAuthProviderResponse[]>>(
    `/organizations/${organizationId}/brands/${brandId}/oauth/providers`,
    FetchMethod.Get
  )
}

export const useGetBrandOAuthProviderApi = (
  organizationId: string,
  brandId: string,
  providerId: string
) => {
  return useCodeerApi<Response<BrandOAuthProviderResponse>>(
    `/organizations/${organizationId}/brands/${brandId}/oauth/providers/${providerId}`,
    FetchMethod.Get
  )
}

export const useCreateBrandOAuthProviderApi = (
  organizationId: string,
  brandId: string
) => {
  return useCodeerApi<
    Response<BrandOAuthProviderResponse>,
    CreateBrandOAuthProviderRequest
  >(
    `/organizations/${organizationId}/brands/${brandId}/oauth/providers`,
    FetchMethod.Post
  )
}

export const useUpdateBrandOAuthProviderApi = (
  organizationId: string,
  brandId: string,
  providerId: string
) => {
  return useCodeerApi<
    Response<BrandOAuthProviderResponse>,
    UpdateBrandOAuthProviderRequest
  >(
    `/organizations/${organizationId}/brands/${brandId}/oauth/providers/${providerId}`,
    FetchMethod.Put
  )
}

export const useDeleteBrandOAuthProviderApi = (
  organizationId: string,
  brandId: string,
  providerId: string
) => {
  return useCodeerApi<Response<unknown>>(
    `/organizations/${organizationId}/brands/${brandId}/oauth/providers/${providerId}`,
    FetchMethod.Delete
  )
}
